import { Upload } from "tus-js-client";

import type { ApiClient } from "../../api";
import {
  deleteApiV1FilesByFile,
  getApiV1Files,
  getApiV1FilesByFile,
  getApiV1FilesByFileDownload,
  getApiV1FilesByFilePreview,
  getApiV1FilesFolders,
  getApiV1FilesFoldersByFolderScopes,
  patchApiV1FilesByFile,
  patchApiV1FilesFoldersByFolder,
  postApiV1FilesByFileConfirm,
  postApiV1FilesByFileMove,
  postApiV1FilesByFileRestore,
  postApiV1FilesFolders,
  postApiV1FilesUploads,
  putApiV1FilesFoldersByFolderScopes,
} from "../../api/generated";
import type {
  CreateFileFolder,
  InitiateFileUpload,
  SignedUpload,
  UpdateFileFolder,
} from "./types";

/** Доля загруженного от нуля до единицы. */
export type UploadProgress = (fraction: number) => void;

/**
 * Токен защиты от подделки запросов.
 *
 * Докачка идёт мимо сгенерированного клиента: tus-js-client отправляет свои
 * запросы сам, поэтому заголовок приходится собирать здесь.
 */
function csrfToken(): string | null {
  const prefix = "XSRF-TOKEN=";
  const cookie = document.cookie
    .split("; ")
    .find((entry) => entry.startsWith(prefix));
  if (cookie === undefined) return null;
  try {
    return decodeURIComponent(cookie.slice(prefix.length));
  } catch {
    return cookie.slice(prefix.length);
  }
}

export class FilesApi {
  /**
   * @param apiBaseUrl адрес API, к которому клиент уже обращается. Нужен
   * докачке: сервер отдаёт путь, а не полный адрес, потому что части обязаны
   * уйти на тот же домен — сессия выдана на него, и на соседнем её нет.
   */
  constructor(
    private readonly client: ApiClient,
    private readonly apiBaseUrl: string = globalThis.location?.origin ?? "/",
  ) {}

  list(query?: {
    readonly page?: number;
    readonly per_page?: number;
    /** Только файлы этой папки; без значения — весь список. */
    readonly folder_id?: string;
  }) {
    return getApiV1Files(this.client, { query });
  }

  /** Все доступные папки плоским списком; дерево собирает клиент. */
  folders(search?: string) {
    return getApiV1FilesFolders(this.client, {
      query: search ? { search } : undefined,
    });
  }

  createFolder(body: CreateFileFolder) {
    return postApiV1FilesFolders(this.client, { body });
  }

  /** Переименовать и/или перенести; `parent_id: null` поднимает папку в корень. */
  updateFolder(id: string, body: UpdateFileFolder) {
    return patchApiV1FilesFoldersByFolder(this.client, {
      path: { folder: id },
      body,
    });
  }

  /** КПО, которым папка открыта дополнительно к организации-владельцу. */
  folderScopes(id: string) {
    return getApiV1FilesFoldersByFolderScopes(this.client, {
      path: { folder: id },
    });
  }

  /** Заменить список КПО целиком: пустой набор закрывает папку для всех, кроме владельца. */
  replaceFolderScopes(id: string, scopeIds: readonly string[]) {
    return putApiV1FilesFoldersByFolderScopes(this.client, {
      path: { folder: id },
      body: { scope_ids: [...scopeIds] },
    });
  }

  /** Переложить файл в папку своей организации; `null` — в корень. */
  move(id: string, folderId: string | null) {
    return postApiV1FilesByFileMove(this.client, {
      path: { file: id },
      body: { folder_id: folderId },
    });
  }

  rename(id: string, filename: string) {
    return patchApiV1FilesByFile(this.client, {
      path: { file: id },
      body: { filename },
    });
  }

  initiate(input: InitiateFileUpload) {
    return postApiV1FilesUploads(this.client, { body: input });
  }

  /**
   * Отправить байты в хранилище.
   *
   * Способ выбирает сервер, а не размер файла на глазок: мелкий файл уходит
   * одним подписанным PUT прямо в хранилище, крупный — частями по TUS через
   * наш сервер, чтобы обрыв связи на середине не начинал загрузку сначала.
   */
  async uploadObject(
    upload: SignedUpload,
    body: Blob,
    onProgress?: UploadProgress,
  ): Promise<void> {
    if (upload.protocol === "tus") {
      return this.uploadResumable(upload, body, onProgress);
    }

    const response = await fetch(upload.url, {
      method: "PUT",
      headers: upload.headers,
      body,
      credentials: "omit",
    });
    if (!response.ok) {
      throw new Error(`Object upload failed with status ${response.status}.`);
    }
    onProgress?.(1);
  }

  /**
   * Докачиваемая загрузка.
   *
   * Адрес загрузки сервер создал вместе с записью файла, поэтому создание
   * здесь не запрашивается: клиент сразу узнаёт достигнутое смещение и
   * досылает недостающее. Повторы на обрывах связи — часть протокола, ошибкой
   * считается только исчерпание попыток.
   */
  private uploadResumable(
    upload: SignedUpload,
    body: Blob,
    onProgress?: UploadProgress,
  ): Promise<void> {
    const apiOrigin = new URL(this.apiBaseUrl).origin;
    const uploadUrl = new URL(upload.url, this.apiBaseUrl);
    if (
      uploadUrl.origin !== apiOrigin ||
      uploadUrl.username ||
      uploadUrl.password
    ) {
      return Promise.reject(new Error("Недопустимый адрес загрузки"));
    }
    const token = csrfToken();

    return new Promise((resolve, reject) => {
      new Upload(body, {
        uploadUrl: uploadUrl.toString(),
        // Повторные запросы протокола тоже не должны унести CSRF на чужой origin.
        onBeforeRequest: (request) => {
          if (new URL(request.getURL(), this.apiBaseUrl).origin !== apiOrigin) {
            throw new Error("Недопустимый адрес загрузки");
          }
        },
        chunkSize: upload.chunk_bytes ?? 16 * 1024 * 1024,
        // Части уходят в хранилище по мере приёма, поэтому досылать их можно
        // только по порядку.
        parallelUploads: 1,
        retryDelays: [0, 1000, 3000, 5000, 10000],
        headers: {
          ...upload.headers,
          "X-Requested-With": "XMLHttpRequest",
          ...(token === null ? {} : { "X-XSRF-TOKEN": token }),
        },
        onProgress: (sent, total) =>
          onProgress?.(total === 0 ? 0 : sent / total),
        onError: (error) => reject(error),
        onSuccess: () => {
          onProgress?.(1);
          resolve();
        },
      }).start();
    });
  }

  confirm(id: string) {
    return postApiV1FilesByFileConfirm(this.client, {
      path: { file: id },
    });
  }

  get(id: string) {
    return getApiV1FilesByFile(this.client, { path: { file: id } });
  }

  download(id: string) {
    return getApiV1FilesByFileDownload(this.client, {
      path: { file: id },
    });
  }

  preview(id: string) {
    return getApiV1FilesByFilePreview(this.client, {
      path: { file: id },
    });
  }

  delete(id: string) {
    return deleteApiV1FilesByFile(this.client, { path: { file: id } });
  }

  restore(id: string) {
    return postApiV1FilesByFileRestore(this.client, {
      path: { file: id },
    });
  }
}
